import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { ArrowLeft, ClipboardList, Edit, Package, Plus, Printer, Radio, Save, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ICS259EquipmentForm = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [view, setView] = useState('list');
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    exercise_name: currentExercise?.exercise_name || '',
    incident_order_number: '',
    office_reference_number: '',
    descriptive_location: '',
    sec: '',
    twn: '',
    rng: '',
    base_mdm: '',
    incident_base: '',
    phone_number: '',
    jurisdiction: '',
    ordering_office: '',
    latitude: '',
    longitude: '',
    bearing: '',
    distance: '',
    base_of_omni: '',
    air_contact: '',
    air_freq: '',
    grd_contact: '',
    grd_freq: '',
    reload_base: '',
    equipment_hazards: '', // Changed from aircraft_hazards
    order_details: [],
    order_relayed: [],
    request_items: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Phone number validation
  const validatePhoneNumber = (phone) => {
    if (!phone || !phone.trim()) return true; // empty is OK
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  };

  // Latitude validation (-90.0000 to 90.0000)
  const validateLatitude = (lat) => {
    const num = parseFloat(lat);
    return !isNaN(num) && num >= -90.0 && num <= 90.0;
  };

  // Longitude validation (-180.0000 to 180.0000) 
  const validateLongitude = (lng) => {
    const num = parseFloat(lng);
    return !isNaN(num) && num >= -180.0 && num <= 180.0;
  };

  // Format phone number
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0,3)}-${digits.slice(3)}`;
    return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6,10)}`;
  };

  // Handle input change with validation
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Real-time validation
    if (field === 'phone_number') {
      // Validate phone format XXX-XXX-XXXX
      const phoneRegex = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/;
      if (value && value.trim() && !phoneRegex.test(value)) {
        setErrors(prev => ({ ...prev, [field]: 'Phone must be in format: 123-456-7890' }));
      }
    } else if (field === 'bearing') {
      // Validate bearing range 001-360
      const bearingNum = parseInt(value);
      if (value && value.trim()) {
        if (isNaN(bearingNum) || bearingNum < 1 || bearingNum > 360) {
          setErrors(prev => ({ ...prev, [field]: 'Bearing must be between 001 and 360 degrees' }));
        }
      }
    } else if (field === 'distance') {
      // Validate distance > 0 with 1 decimal place
      const distanceNum = parseFloat(value);
      if (value && value.trim()) {
        if (isNaN(distanceNum) || distanceNum <= 0) {
          setErrors(prev => ({ ...prev, [field]: 'Distance must be greater than 0' }));
        } else {
          // Format to 1 decimal place
          const formatted = parseFloat(distanceNum.toFixed(1));
          if (formatted !== distanceNum) {
            setFormData(prev => ({ ...prev, [field]: formatted.toString() }));
          }
        }
      }
    } else if (field === 'latitude') {
      if (value && !validateLatitude(value)) {
        setErrors(prev => ({ ...prev, [field]: 'Latitude must be between -90.0000 and 90.0000' }));
      }
    } else if (field === 'longitude') {
      if (value && !validateLongitude(value)) {
        setErrors(prev => ({ ...prev, [field]: 'Longitude must be between -180.0000 and 180.0000' }));
      }
    }
  };

  // Fetch all ICS 259 Equipment entries (will use same API endpoint)
  const fetchEntries = async () => {
    if (!currentExercise?.id) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/ics-259?exercise_id=${currentExercise.id}&form_type=equipment`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Error fetching ICS 259 Equipment entries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [currentExercise?.id]);

  // Initialize form with editing data
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        ...editingEntry,
        exercise_name: editingEntry.exercise_name || currentExercise?.exercise_name || '',
        equipment_hazards: editingEntry.aircraft_hazards || '' // Map aircraft_hazards to equipment_hazards
      });
    } else {
      setFormData({
        exercise_name: currentExercise?.exercise_name || '',
        incident_order_number: '',
        office_reference_number: '',
        descriptive_location: '',
        sec: '',
        twn: '',
        rng: '',
        base_mdm: '',
        incident_base: '',
        phone_number: '',
        jurisdiction: '',
        ordering_office: '',
        latitude: '',
        longitude: '',
        bearing: '',
        distance: '',
        base_of_omni: '',
        air_contact: '',
        air_freq: '',
        grd_contact: '',
        grd_freq: '',
        reload_base: '',
        equipment_hazards: '',
        order_details: [],
        order_relayed: [],
        request_items: []
      });
    }
  }, [editingEntry, currentExercise]);

  // Add new order detail
  const addOrderDetail = () => {
    const newDetail = {
      req_num: '',
      ordered_datetime: '',
      from_location: '',
      to_location: '',
      qty: 0,
      res_reqstd: '',
      date_needed: '',
      deliver_to: '',
      to_field: '',
      from_field: '',
      time: '',
      agency_id: '',
      res_assigned: '',
      etd: '',
      eta: '',
      released: '',
      rels_to: '',
      rels_date: '',
      rels_time: '',
      rels_eta: ''
    };
    setFormData(prev => ({
      ...prev,
      order_details: [...prev.order_details, newDetail]
    }));
  };

  // Add new order relayed entry
  const addOrderRelayed = () => {
    const newRelayed = {
      reg_no: '',
      date_time: '',
      to: '',
      from_location: '',
      action_taken: ''
    };
    setFormData(prev => ({
      ...prev,
      order_relayed: [...prev.order_relayed, newRelayed]
    }));
  };

  // Add new request item
  const addRequestItem = () => {
    const newRequest = {
      request_number: formData.request_items.length + 1,
      remarks: ''
    };
    setFormData(prev => ({
      ...prev,
      request_items: [...prev.request_items, newRequest]
    }));
  };

  // Remove functions
  const removeOrderDetail = (index) => {
    setFormData(prev => ({
      ...prev,
      order_details: prev.order_details.filter((_, i) => i !== index)
    }));
  };

  const removeOrderRelayed = (index) => {
    setFormData(prev => ({
      ...prev,
      order_relayed: prev.order_relayed.filter((_, i) => i !== index)
    }));
  };

  const removeRequestItem = (index) => {
    setFormData(prev => ({
      ...prev,
      request_items: prev.request_items.filter((_, i) => i !== index).map((item, i) => ({
        ...item,
        request_number: i + 1 // Re-number items
      }))
    }));
  };

  // Update functions for repeatable sections
  const updateOrderDetail = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      order_details: prev.order_details.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const updateOrderRelayed = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      order_relayed: prev.order_relayed.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const updateRequestItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      request_items: prev.request_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (formData.phone_number && !validatePhoneNumber(formData.phone_number)) {
      newErrors.phone_number = 'Please enter a valid 10-digit phone number';
    }
    
    if (formData.latitude && !validateLatitude(formData.latitude)) {
      newErrors.latitude = 'Latitude must be between -90.0000 and 90.0000';
    }
    
    if (formData.longitude && !validateLongitude(formData.longitude)) {
      newErrors.longitude = 'Longitude must be between -180.0000 and 180.0000';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save (using same API endpoint with equipment-specific data)
  const handleSave = async () => {
    if (!currentExercise?.id || !validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        exercise_id: currentExercise.id,
        ...formData,
        aircraft_hazards: formData.equipment_hazards, // Map equipment_hazards back to aircraft_hazards for API
        form_type: 'equipment' // Add identifier for equipment vs aircraft orders
      };

      let response;
      if (editingEntry) {
        response = await fetch(`${API}/ics-259/${editingEntry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/ics-259`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        await fetchEntries();
        setView('list');
        setEditingEntry(null);
      } else {
        console.error('Error saving ICS 259 Equipment entry');
      }
    } catch (error) {
      console.error('Error saving ICS 259 Equipment entry:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this ICS 259 Equipment entry?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/ics-259/${entryId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        await fetchEntries();
      }
    } catch (error) {
      console.error('Error deleting ICS 259 Equipment entry:', error);
    } finally {
      setLoading(false);
    }
  };

  // Print function (adapted for equipment)
  const handlePrint = (entry = null) => {
    const entryToPrint = entry || formData;

    // Build print tables for repeating sections (Order Details / Relayed / Request Items)
    const orderDetailsHtml = (entry) => {
      const rows = entry.order_details || [];
      if (rows.length === 0) {
        return `<div style="margin-bottom: 20px; padding: 15px; border: 2px solid #000;">
          <h4 style="margin-bottom: 10px; font-weight: bold;">ORDER DETAILS</h4>
          <p style="color: #666; font-style: italic;">No order details recorded.</p>
        </div>`;
      }
      const headers = ['Req #', 'Ordered', 'From', 'To', 'Qty', 'Res Reqstd', 'Date Needed', 'Deliver To', 'Agency ID', 'Res Assigned', 'ETD', 'ETA', 'Released', 'Rels To', 'Rels Date', 'Rels Time', 'Rels ETA'];
      const keys = ['req_num','ordered_datetime','from_location','to_location','qty','res_reqstd','date_needed','deliver_to','agency_id','res_assigned','etd','eta','released','rels_to','rels_date','rels_time','rels_eta'];
      const body = rows.map(r => `<tr>${keys.map(k => `<td style="border:1px solid #000; padding:4px;">${r[k] != null && r[k] !== '' ? r[k] : ''}</td>`).join('')}</tr>`).join('');
      return `<div style="margin-bottom: 20px; padding: 15px; border: 2px solid #000;">
        <h4 style="margin-bottom: 10px; font-weight: bold;">ORDER DETAILS (${rows.length})</h4>
        <table style="width:100%; border-collapse: collapse; font-size: 10px;">
          <thead><tr>${headers.map(h => `<th style="border:1px solid #000; padding:4px; background:#eee;">${h}</th>`).join('')}</tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>`;
    };

    const orderRelayedHtml = (entry) => {
      const rows = entry.order_relayed || [];
      if (rows.length === 0) {
        return `<div style="margin-bottom: 20px; padding: 15px; border: 2px solid #000;">
          <h4 style="margin-bottom: 10px; font-weight: bold;">ORDER RELAYED</h4>
          <p style="color: #666; font-style: italic;">No relayed orders recorded.</p>
        </div>`;
      }
      const headers = ['Reg #', 'Date', 'Time', 'To', 'From', 'Action Taken'];
      const body = rows.map(r => {
        const dt = (r.date_time || '').split('T');
        const cells = [r.reg_no || '', dt[0] || '', dt[1] || '', r.to || '', r.from_location || '', r.action_taken || ''];
        return `<tr>${cells.map(v => `<td style="border:1px solid #000; padding:4px;">${v}</td>`).join('')}</tr>`;
      }).join('');
      return `<div style="margin-bottom: 20px; padding: 15px; border: 2px solid #000;">
        <h4 style="margin-bottom: 10px; font-weight: bold;">ORDER RELAYED (${rows.length})</h4>
        <table style="width:100%; border-collapse: collapse; font-size: 11px;">
          <thead><tr>${headers.map(h => `<th style="border:1px solid #000; padding:4px; background:#eee;">${h}</th>`).join('')}</tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>`;
    };

    const requestItemsHtml = (entry) => {
      const rows = entry.request_items || [];
      if (rows.length === 0) {
        return `<div style="margin-bottom: 20px; padding: 15px; border: 2px solid #000;">
          <h4 style="margin-bottom: 10px; font-weight: bold;">REQUEST ITEMS</h4>
          <p style="color: #666; font-style: italic;">No request items recorded.</p>
        </div>`;
      }
      const headers = ['Request #', 'Remarks'];
      const keys = ['request_number','remarks'];
      const body = rows.map(r => `<tr>${keys.map(k => `<td style="border:1px solid #000; padding:4px;">${r[k] != null && r[k] !== '' ? r[k] : ''}</td>`).join('')}</tr>`).join('');
      return `<div style="margin-bottom: 20px; padding: 15px; border: 2px solid #000;">
        <h4 style="margin-bottom: 10px; font-weight: bold;">REQUEST ITEMS (${rows.length})</h4>
        <table style="width:100%; border-collapse: collapse; font-size: 11px;">
          <thead><tr>${headers.map(h => `<th style="border:1px solid #000; padding:4px; background:#eee;">${h}</th>`).join('')}</tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>`;
    };

    const printContent = `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <div style="text-align: center; margin-bottom: 20px;">
          <h2>ICS 259 - RESOURCE ORDER EQUIPMENT</h2>
          <h3>Exercise: ${entryToPrint.exercise_name}</h3>
        </div>
        
        <div style="margin-bottom: 20px; padding: 15px; border: 2px solid #000;">
          <h4 style="margin-bottom: 10px; font-weight: bold;">INCIDENT INFORMATION</h4>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 5px; font-weight: bold;">Incident Order Number:</td>
              <td style="padding: 5px;">${entryToPrint.incident_order_number}</td>
              <td style="padding: 5px; font-weight: bold;">Office Reference Number:</td>
              <td style="padding: 5px;">${entryToPrint.office_reference_number}</td>
            </tr>
            <tr>
              <td style="padding: 5px; font-weight: bold;">Descriptive Location:</td>
              <td colspan="3" style="padding: 5px;">${entryToPrint.descriptive_location}</td>
            </tr>
            <tr>
              <td style="padding: 5px; font-weight: bold;">Sec:</td>
              <td style="padding: 5px;">${entryToPrint.sec}</td>
              <td style="padding: 5px; font-weight: bold;">TWN:</td>
              <td style="padding: 5px;">${entryToPrint.twn}</td>
            </tr>
            <tr>
              <td style="padding: 5px; font-weight: bold;">RNG:</td>
              <td style="padding: 5px;">${entryToPrint.rng}</td>
              <td style="padding: 5px; font-weight: bold;">Base MDM:</td>
              <td style="padding: 5px;">${entryToPrint.base_mdm}</td>
            </tr>
            <tr>
              <td style="padding: 5px; font-weight: bold;">Incident Base:</td>
              <td style="padding: 5px;">${entryToPrint.incident_base}</td>
              <td style="padding: 5px; font-weight: bold;">Phone Number:</td>
              <td style="padding: 5px;">${entryToPrint.phone_number}</td>
            </tr>
            <tr>
              <td style="padding: 5px; font-weight: bold;">Jurisdiction:</td>
              <td style="padding: 5px;">${entryToPrint.jurisdiction}</td>
              <td style="padding: 5px; font-weight: bold;">Ordering Office:</td>
              <td style="padding: 5px;">${entryToPrint.ordering_office}</td>
            </tr>
          </table>
        </div>
        
        <div style="margin-bottom: 20px; padding: 15px; border: 2px solid #000;">
          <h4 style="margin-bottom: 10px; font-weight: bold;">EQUIPMENT DETAILS</h4>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 5px; font-weight: bold;">Latitude:</td>
              <td style="padding: 5px;">${entryToPrint.latitude || ''}</td>
              <td style="padding: 5px; font-weight: bold;">Longitude:</td>
              <td style="padding: 5px;">${entryToPrint.longitude || ''}</td>
            </tr>
            <tr>
              <td style="padding: 5px; font-weight: bold;">Bearing:</td>
              <td style="padding: 5px;">${entryToPrint.bearing}</td>
              <td style="padding: 5px; font-weight: bold;">Distance:</td>
              <td style="padding: 5px;">${entryToPrint.distance}</td>
            </tr>
            <tr>
              <td style="padding: 5px; font-weight: bold;">Base of OMNI:</td>
              <td style="padding: 5px;">${entryToPrint.base_of_omni}</td>
              <td style="padding: 5px; font-weight: bold;">Air Contact:</td>
              <td style="padding: 5px;">${entryToPrint.air_contact}</td>
            </tr>
            <tr>
              <td style="padding: 5px; font-weight: bold;">Air Freq:</td>
              <td style="padding: 5px;">${entryToPrint.air_freq}</td>
              <td style="padding: 5px; font-weight: bold;">Ground Contact:</td>
              <td style="padding: 5px;">${entryToPrint.grd_contact}</td>
            </tr>
            <tr>
              <td style="padding: 5px; font-weight: bold;">Ground Freq:</td>
              <td style="padding: 5px;">${entryToPrint.grd_freq}</td>
              <td style="padding: 5px; font-weight: bold;">Reload Base:</td>
              <td style="padding: 5px;">${entryToPrint.reload_base}</td>
            </tr>
            <tr>
              <td style="padding: 5px; font-weight: bold;">Equipment Hazards:</td>
              <td colspan="3" style="padding: 5px;">${entryToPrint.equipment_hazards || entryToPrint.aircraft_hazards || ''}</td>
            </tr>
          </table>
        </div>
        
        ${orderDetailsHtml(entryToPrint)}
        ${orderRelayedHtml(entryToPrint)}
        ${requestItemsHtml(entryToPrint)}

        <div style="margin-top: 30px; text-align: center; font-size: 12px; color: #666;">
          Generated by EXRSIM - Emergency Response Simulation Platform
        </div>
      </div>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, `
      <html>
        <head>
          <title>ICS 259 - Resource Order Equipment</title>
          <style>
            @media print {
              body { margin: 0; }
              @page { margin: 1in; }
            }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          ${printContent}
        </body>
      </html>
    `);
  };

  // List view
  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <Package className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>
              ICS 259 - Resource Order Equipment
            </h2>
            <p className={`${theme.colors.textMuted} mb-6 max-w-2xl mx-auto`}>
              Manage equipment resource orders and deployment requests for emergency operations.
            </p>
          </div>

          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-lg font-semibold ${theme.colors.textPrimary}`}>
              Equipment Order Records
            </h3>
            <Button
              onClick={() => setView('add')}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First ICS 259 Equipment
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className={`mt-2 ${theme.colors.textMuted}`}>Loading entries...</p>
            </div>
          ) : entries.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-8 text-center">
                <Package className={`h-12 w-12 ${theme.colors.textMuted} mx-auto mb-4`} />
                <h3 className={`text-lg font-medium ${theme.colors.textPrimary} mb-2`}>
                  No ICS 259 Equipment entries yet
                </h3>
                <p className={`${theme.colors.textMuted} mb-4`}>
                  Create your first equipment order to begin managing equipment resource requests.
                </p>
                <Button
                  onClick={() => setView('add')}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create First ICS 259 Equipment
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {entries.map((entry) => (
                <Card key={entry.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-white mb-2">
                          Exercise: {entry.exercise_name}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          Order #: {entry.incident_order_number} • Location: {entry.descriptive_location}
                        </p>
                        <p className="text-gray-400 text-sm">
                          Created: {new Date(entry.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrint(entry)}
                          className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingEntry(entry);
                            setView('edit');
                          }}
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(entry.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Form view (add/edit) - Same structure as aircraft form but with equipment-specific labels
  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => {
                setView('list');
                setEditingEntry(null);
              }}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to List
            </Button>
            <h2 className={`text-2xl font-bold ${theme.colors.textPrimary}`}>
              {editingEntry ? 'Edit' : 'Create'} ICS 259 - Resource Order Equipment
            </h2>
          </div>
          <Button
            onClick={() => handlePrint()}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Printer className="h-4 w-4 mr-2" />
            PRINT ICS 259 Equipment
          </Button>
        </div>

        {/* Heading Card - Exercise and Incident Information */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Exercise & Incident Information</CardTitle>
            <Button onClick={handleSave} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white" size="sm">
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Exercise Name Display */}
            <div>
              <Label className="text-white text-lg font-medium">
                Exercise Name: {formData.exercise_name}
              </Label>
            </div>
            
            {/* First row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-300">Incident Order Number</Label>
                <Input
                  type="number"
                  value={formData.incident_order_number}
                  onChange={(e) => handleInputChange('incident_order_number', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                  placeholder="Order #"
                />
              </div>
              <div>
                <Label className="text-gray-300">Office Reference Number</Label>
                <Input
                  type="number"
                  value={formData.office_reference_number}
                  onChange={(e) => handleInputChange('office_reference_number', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                  placeholder="Reference #"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-gray-300">Descriptive Location</Label>
                <Input
                  value={formData.descriptive_location}
                  onChange={(e) => handleInputChange('descriptive_location', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                  placeholder="Location description"
                />
              </div>
            </div>
            
            {/* Second row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-300">Sec (Section)</Label>
                <Input
                  value={formData.sec}
                  onChange={(e) => handleInputChange('sec', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">TWN (Township)</Label>
                <Input
                  value={formData.twn}
                  onChange={(e) => handleInputChange('twn', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">RNG (Range)</Label>
                <Input
                  type="number"
                  value={formData.rng}
                  onChange={(e) => handleInputChange('rng', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                  placeholder="Enter range number"
                />
              </div>
              <div>
                <Label className="text-gray-300">Base MDM (Base Meridian)</Label>
                <Input
                  value={formData.base_mdm}
                  onChange={(e) => handleInputChange('base_mdm', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
            </div>
            
            {/* Third row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-300">Incident Base</Label>
                <Input
                  value={formData.incident_base}
                  onChange={(e) => handleInputChange('incident_base', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">Phone Number</Label>
                <Input
                  value={formData.phone_number}
                  onChange={(e) => {
                    // Only allow digits and auto-format as XXX-XXX-XXXX
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    let formatted = '';
                    if (digits.length > 0) {
                      formatted = digits.slice(0, 3);
                      if (digits.length > 3) {
                        formatted += '-' + digits.slice(3, 6);
                      }
                      if (digits.length > 6) {
                        formatted += '-' + digits.slice(6, 10);
                      }
                    }
                    if (formatted.length <= 12) {
                      handleInputChange('phone_number', formatted);
                    }
                  }}
                  className={`bg-gray-900 border-gray-600 text-white placeholder-gray-400 ${
                    errors.phone_number ? 'border-red-500' : ''
                  }`}
                  placeholder="123-456-7890"
                  maxLength="12"
                />
                {errors.phone_number && (
                  <p className="text-red-400 text-xs mt-1">{errors.phone_number}</p>
                )}
              </div>
              <div>
                <Label className="text-gray-300">Jurisdiction</Label>
                <Input
                  value={formData.jurisdiction}
                  onChange={(e) => handleInputChange('jurisdiction', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">Ordering Office</Label>
                <Input
                  value={formData.ordering_office}
                  onChange={(e) => handleInputChange('ordering_office', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Aircraft Location Card */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-orange-500">Aircraft Location</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">
                  Latitude 
                  <span className="text-xs text-gray-400 ml-1">(Format: 47.0000, Range: -90.0000 to 90.0000)</span>
                </Label>
                <Input
                  value={formData.latitude}
                  onChange={(e) => handleInputChange('latitude', e.target.value)}
                  className={`bg-gray-900 border-gray-600 text-white placeholder-gray-400 ${
                    errors.latitude ? 'border-red-500' : ''
                  }`}
                  placeholder="47.0000"
                />
                {errors.latitude && (
                  <p className="text-red-400 text-xs mt-1">{errors.latitude}</p>
                )}
              </div>
              <div>
                <Label className="text-gray-300">
                  Longitude 
                  <span className="text-xs text-gray-400 ml-1">(Format: -97.0000, Range: -180.0000 to 180.0000)</span>
                </Label>
                <Input
                  value={formData.longitude}
                  onChange={(e) => handleInputChange('longitude', e.target.value)}
                  className={`bg-gray-900 border-gray-600 text-white placeholder-gray-400 ${
                    errors.longitude ? 'border-red-500' : ''
                  }`}
                  placeholder="-97.0000"
                />
                {errors.longitude && (
                  <p className="text-red-400 text-xs mt-1">{errors.longitude}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Aircraft Details Card */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-orange-500">Aircraft Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-300">Bearing</Label>
                <Input
                  type="number"
                  min="1"
                  max="360"
                  placeholder="001 to 360"
                  value={formData.bearing}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 360)) {
                      handleInputChange('bearing', value);
                    }
                  }}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">Distance</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0.1"
                  placeholder="enter distance to aircraft"
                  value={formData.distance}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty or valid positive number with max 1 decimal place
                    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                      const numValue = parseFloat(value);
                      if (value === '' || (numValue > 0)) {
                        handleInputChange('distance', value);
                      }
                    }
                  }}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">Base of OMNI</Label>
                <Input
                  placeholder="source point for bearing / distance"
                  value={formData.base_of_omni}
                  onChange={(e) => handleInputChange('base_of_omni', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-300">Air Contact</Label>
                <Input
                  placeholder="name"
                  value={formData.air_contact}
                  onChange={(e) => handleInputChange('air_contact', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">Freq</Label>
                <Input
                  placeholder="frequency or channel"
                  value={formData.air_freq}
                  onChange={(e) => handleInputChange('air_freq', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">Grd Contact</Label>
                <Input
                  placeholder="name"
                  value={formData.grd_contact}
                  onChange={(e) => handleInputChange('grd_contact', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">Freq</Label>
                <Input
                  placeholder="frequency or channel"
                  value={formData.grd_freq}
                  onChange={(e) => handleInputChange('grd_freq', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Reload Base</Label>
                <Input
                  placeholder="forwarding operating base"
                  value={formData.reload_base}
                  onChange={(e) => handleInputChange('reload_base', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">Equipment Hazards</Label>
                <Input
                  placeholder="risks and hazards to equipment"
                  value={formData.equipment_hazards}
                  onChange={(e) => handleInputChange('equipment_hazards', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resource Requested Card (Repeatable) */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader className="pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="text-orange-500">Resource Requested</CardTitle>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white" size="sm">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button
                  onClick={addOrderDetail}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Order Entry
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {formData.order_details.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No order details added yet. Click "Add Order Entry" to begin.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {formData.order_details.map((detail, index) => (
                  <div key={`ics259-${index}`} className="border border-gray-600 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-white font-medium">Order Entry {index + 1}</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeOrderDetail(index)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* First row of order details - KEEP THESE */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <Label className="text-gray-300">Req Num</Label>
                        <Input
                          type="number"
                          value={detail.req_num}
                          onChange={(e) => updateOrderDetail(index, 'req_num', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                          placeholder="Enter request number"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Ordered Date/Time</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={detail.ordered_datetime ? detail.ordered_datetime.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const t = detail.ordered_datetime ? detail.ordered_datetime.split('T')[1] || '' : '';
                              updateOrderDetail(index, 'ordered_datetime', date ? `${date}T${t}` : '');
                            }}
                            className="bg-gray-900 border-gray-600 text-white"
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={detail.ordered_datetime ? detail.ordered_datetime.split('T')[1] || '' : ''}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^0-9:]/g, '');
                              if (v.length === 2 && !v.includes(':')) v = v + ':';
                              if (v.length <= 5) {
                                const date = detail.ordered_datetime ? detail.ordered_datetime.split('T')[0] : '';
                                updateOrderDetail(index, 'ordered_datetime', date ? `${date}T${v}` : v);
                              }
                            }}
                            className="bg-gray-900 border-gray-600 text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-300">From</Label>
                        <Input
                          type="text"
                          value={detail.from_location}
                          onChange={(e) => updateOrderDetail(index, 'from_location', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">To</Label>
                        <Input
                          type="text"
                          value={detail.to_location}
                          onChange={(e) => updateOrderDetail(index, 'to_location', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <Label className="text-gray-300">Qty</Label>
                        <Input
                          type="number"
                          value={detail.qty}
                          onChange={(e) => updateOrderDetail(index, 'qty', parseInt(e.target.value) || 0)}
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Res Reqstd</Label>
                        <Input
                          value={detail.res_reqstd}
                          onChange={(e) => updateOrderDetail(index, 'res_reqstd', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Date Needed</Label>
                        <Input
                          type="date"
                          value={detail.date_needed}
                          onChange={(e) => updateOrderDetail(index, 'date_needed', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Deliver to</Label>
                        <Input
                          value={detail.deliver_to}
                          onChange={(e) => updateOrderDetail(index, 'deliver_to', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Resource Assigned Card */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader className="pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="text-orange-500">Resource Assigned</CardTitle>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white" size="sm">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button
                  onClick={addOrderDetail}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Order Entry
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {formData.order_details.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Add order details above to manage resource assignments.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {formData.order_details.map((detail, index) => (
                  <div key={`ics259-${index}`} className="border border-gray-600 rounded-lg p-4">
                    <div className="mb-4">
                      <h4 className="text-white font-medium">Resource Assignment {index + 1}</h4>
                    </div>
                    
                    {/* Resource assignment fields */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <Label className="text-gray-300">To:</Label>
                        <Input
                          type="text"
                          value={detail.to_field}
                          onChange={(e) => updateOrderDetail(index, 'to_field', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">From:</Label>
                        <Input
                          type="text"
                          value={detail.from_field}
                          onChange={(e) => updateOrderDetail(index, 'from_field', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Time</Label>
                        <Input
                          type="text"
                          placeholder="HH:MM"
                          pattern="[0-2][0-9]:[0-5][0-9]"
                          maxLength="5"
                          value={detail.time}
                          onChange={(e) => {
                            let v = e.target.value.replace(/[^0-9:]/g, '');
                            if (v.length === 2 && !v.includes(':')) v = v + ':';
                            if (v.length <= 5) updateOrderDetail(index, 'time', v);
                          }}
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Agency ID</Label>
                        <Input
                          value={detail.agency_id}
                          onChange={(e) => updateOrderDetail(index, 'agency_id', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <Label className="text-gray-300">Res Assigned</Label>
                        <Input
                          value={detail.res_assigned}
                          onChange={(e) => updateOrderDetail(index, 'res_assigned', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">ETD</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={detail.etd ? detail.etd.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const t = detail.etd ? detail.etd.split('T')[1] || '' : '';
                              updateOrderDetail(index, 'etd', date ? `${date}T${t}` : '');
                            }}
                            className="bg-gray-900 border-gray-600 text-white"
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={detail.etd ? detail.etd.split('T')[1] || '' : ''}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^0-9:]/g, '');
                              if (v.length === 2 && !v.includes(':')) v = v + ':';
                              if (v.length <= 5) {
                                const date = detail.etd ? detail.etd.split('T')[0] : '';
                                updateOrderDetail(index, 'etd', date ? `${date}T${v}` : v);
                              }
                            }}
                            className="bg-gray-900 border-gray-600 text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-300">ETA</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={detail.eta ? detail.eta.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const t = detail.eta ? detail.eta.split('T')[1] || '' : '';
                              updateOrderDetail(index, 'eta', date ? `${date}T${t}` : '');
                            }}
                            className="bg-gray-900 border-gray-600 text-white"
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={detail.eta ? detail.eta.split('T')[1] || '' : ''}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^0-9:]/g, '');
                              if (v.length === 2 && !v.includes(':')) v = v + ':';
                              if (v.length <= 5) {
                                const date = detail.eta ? detail.eta.split('T')[0] : '';
                                updateOrderDetail(index, 'eta', date ? `${date}T${v}` : v);
                              }
                            }}
                            className="bg-gray-900 border-gray-600 text-white"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-gray-300">Released/Rels to:</Label>
                        <Input
                          value={detail.rels_to}
                          onChange={(e) => updateOrderDetail(index, 'rels_to', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Rels Date</Label>
                        <Input
                          type="date"
                          value={detail.rels_date}
                          onChange={(e) => updateOrderDetail(index, 'rels_date', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Time</Label>
                        <Input
                          type="text" pattern="[0-2][0-9]:[0-5][0-9]" placeholder="HH:MM (24-hour)" maxLength="5"
                          value={detail.rels_time}
                          onChange={(e) => updateOrderDetail(index, 'rels_time', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">ETA</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={detail.rels_eta ? detail.rels_eta.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const t = detail.rels_eta ? detail.rels_eta.split('T')[1] || '' : '';
                              updateOrderDetail(index, 'rels_eta', date ? `${date}T${t}` : '');
                            }}
                            className="bg-gray-900 border-gray-600 text-white"
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={detail.rels_eta ? detail.rels_eta.split('T')[1] || '' : ''}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^0-9:]/g, '');
                              if (v.length === 2 && !v.includes(':')) v = v + ':';
                              if (v.length <= 5) {
                                const date = detail.rels_eta ? detail.rels_eta.split('T')[0] : '';
                                updateOrderDetail(index, 'rels_eta', date ? `${date}T${v}` : v);
                              }
                            }}
                            className="bg-gray-900 border-gray-600 text-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Relayed Card */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader className="pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="text-orange-500">Order Relayed</CardTitle>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white" size="sm">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button
                  onClick={addOrderRelayed}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Order Relayed
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {formData.order_relayed.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Radio className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No order relayed entries added yet. Click "Add Order Relayed" to begin.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.order_relayed.map((relayed, index) => (
                  <div key={`ics259-${index}`} className="border border-gray-600 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-white font-medium">Order Relayed {index + 1}</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeOrderRelayed(index)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <Label className="text-gray-300">Reg No.</Label>
                        <Input
                          value={relayed.reg_no}
                          onChange={(e) => updateOrderRelayed(index, 'reg_no', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Date:</Label>
                        <Input
                          type="date"
                          value={relayed.date_time ? relayed.date_time.split('T')[0] : ''}
                          onChange={(e) => {
                            const date = e.target.value;
                            const t = relayed.date_time ? relayed.date_time.split('T')[1] || '' : '';
                            updateOrderRelayed(index, 'date_time', date ? `${date}T${t}` : t);
                          }}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Time:</Label>
                        <Input
                          type="text"
                          placeholder="HH:MM"
                          pattern="[0-2][0-9]:[0-5][0-9]"
                          maxLength="5"
                          value={relayed.date_time ? relayed.date_time.split('T')[1] || '' : ''}
                          onChange={(e) => {
                            let v = e.target.value.replace(/[^0-9:]/g, '');
                            if (v.length === 2 && !v.includes(':')) v = v + ':';
                            if (v.length <= 5) {
                              const date = relayed.date_time ? relayed.date_time.split('T')[0] : '';
                              updateOrderRelayed(index, 'date_time', date ? `${date}T${v}` : v);
                            }
                          }}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">To:</Label>
                        <Input
                          type="text"
                          value={relayed.to}
                          onChange={(e) => updateOrderRelayed(index, 'to', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                          placeholder="To"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">From:</Label>
                        <Input
                          type="text"
                          value={relayed.from_location}
                          onChange={(e) => updateOrderRelayed(index, 'from_location', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                          placeholder="From"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Action Taken</Label>
                      <Textarea
                        value={relayed.action_taken}
                        onChange={(e) => updateOrderRelayed(index, 'action_taken', e.target.value)}
                        className="bg-gray-900 border-gray-600 text-white placeholder-gray-400"
                        rows={3}
                        placeholder="Describe the action taken..."
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Remarks Card */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader className="pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="text-orange-500">Remarks</CardTitle>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white" size="sm">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button
                  onClick={addRequestItem}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Request
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {formData.request_items.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No request items added yet. Click "Add Request" to begin.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.request_items.map((request, index) => (
                  <div key={`ics259-${index}`} className="border border-gray-600 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-white font-medium">Request {request.request_number}</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeRequestItem(index)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="flex gap-4">
                      <div style={{ width: '150px' }}>
                        <Label className="text-gray-300">Request Number</Label>
                        <Input
                          type="number"
                          value={request.request_number}
                          onChange={(e) => updateRequestItem(index, 'request_number', parseInt(e.target.value) || 0)}
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-gray-300">Remarks</Label>
                        <Textarea
                          value={request.remarks}
                          onChange={(e) => updateRequestItem(index, 'remarks', e.target.value)}
                          className="bg-gray-900 border-gray-600 text-white placeholder-gray-400 resize-none"
                          rows={1}
                          placeholder="Additional remarks..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingEntry(null);
            }}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingEntry ? 'Update' : 'Save')} ICS 259 Equipment
          </Button>
        </div>
      </div>
    </div>
  );
};

// ICS 259 - Resource Order Overhead Form (Copied and adapted from Aircraft form)

export default ICS259EquipmentForm;
